import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  convertLabelMapToConfig,
  convertLabelUomMapToConfig,
  mergeConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import { getSieveSizeFromId } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { SieveSizeId } from 'Data/ViewModels/Equipment/ISieveSize';
import { get, joinPaths } from 'Lib/Object/Path/shared';

const getLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.Preparation.InitialSeparationPerformed`]: 'Coarsest separation performed prior to sieving',
      [`${prefix}.Preparation.MaterialWashed`]: 'Material Washed',
      [`${prefix}.Preparation.TestStandardAndMethod`]: 'Test Method',
      [`${prefix}.Preparation.AddSieveSizesBasedOnSpecification`]: 'Sieves Are Added',

      [`${prefix}.WholePortion.Preparation.StateBeforePreparation`]: 'Condition of Received Sample',
      [`${prefix}.WholePortion.Preparation.PreparationMethod`]: 'Material Preparation Method',

      [`${prefix}.WholePortion.Masses.MassMeasurement.Equipment.Balance`]: 'Balance',
      [`${prefix}.WholePortion.Masses.MassMeasurement.Equipment.DryMassBalance`]: 'Dry Mass Balance',
      [`${prefix}.WholePortion.Masses.MassMeasurement.Equipment.Oven`]: 'Oven',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareID`]: 'Tare ID',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareMass`]: 'Tare Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareAndWetMass`]: 'Tare and Material Wet Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.WetMass`]: 'Material Wet Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareAndDryMass`]: 'Tare and Material Dry Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.DryMass`]: 'Material Dry Mass',
      [`${prefix}.WholePortion.Masses.DryMass`]: 'Total Material Dry Mass',
      [`${prefix}.WholePortion.Masses.MoistureContent`]: 'Water Content',
      [`${prefix}.WholePortion.Masses.AssociatedMoistureContent.ExternalTest.Results.MoistureContentPercent`]:
        'Water Content',

      [`${prefix}.WholePortion.Masses.MassMeasurement.WetMassMeasurements.TareMass`]: 'Tare Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.WetMassMeasurements.TareAndWetMass`]:
        'Tare and Material Wet Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.WetMassMeasurements.WetMass`]: 'Material Wet Mass',
      [`${prefix}.WholePortion.Pretreatment.UntreatedMaterialDryMass`]: 'Untreated Material Dry Mass',
      [`${prefix}.WholePortion.Pretreatment.Reagent`]: 'Reagent Used',
      [`${prefix}.WholePortion.Pretreatment.SeparationMethod`]: 'Separation of Soil and Reagent by',
      [`${prefix}.WholePortion.Pretreatment.Equipment.Balance`]: 'Pre-Treated Material Balance',
      [`${prefix}.WholePortion.Pretreatment.MeasuredConstantDryMass.Equipment.Balance`]: 'Pre-Treated Material Balance',
      [`${prefix}.WholePortion.Pretreatment.TareID`]: 'Tare ID',
      [`${prefix}.WholePortion.Pretreatment.TareMass`]: 'Tare Mass',
      [`${prefix}.WholePortion.Pretreatment.TareAndPretreatedMaterialDryMass`]:
        'Tare and Pre-Treated Material Dry Mass',
      [`${prefix}.WholePortion.Pretreatment.PretreatedMaterialDryMass`]: 'Pre-Treated Material Dry Mass',

      [`${prefix}.WholePortion.Sieving.Equipment.Balance`]: 'Balance',
      [`${prefix}.WholePortion.Sieving.Equipment.SieveSet`]: 'Sieve Set',

      [`${prefix}.Results.ChartOptions.CurveFittingAlgorithm`]: 'Curve Fitting',
      [`${prefix}.Results.ChartOptions.JoinSpecificationPoints`]: 'Join specification points on chart',

      [`${prefix}.Results.D85`]: 'D85',
      [`${prefix}.Results.D60`]: 'D60',
      [`${prefix}.Results.D50`]: 'D50',
      [`${prefix}.Results.D30`]: 'D30',
      [`${prefix}.Results.D15`]: 'D15',
      [`${prefix}.Results.D10`]: 'D10',
      [`${prefix}.Results.CoefficientOfUniformity`]: 'Coefficient of Uniformity (Cu)',
      [`${prefix}.Results.CoefficientOfCurvature`]: 'Coefficient of Curvature (Cc)',

      [`${prefix}.Results.ReportPSDCurveAnalysis`]: 'Report D60, D30, D10, Cu and Cc',

      [`${prefix}.Results.PercentagePassingWashingSieve`]: (args: IConfigFunctionArgs) => {
        const washingSieveSizeId: SieveSizeId = '0_063';
        const { AllSieveSizes: allSieveSizes } = get(args.worksheet, joinPaths(prefix, 'Equipment')) ?? {};
        return `Finer than ${getSieveSizeFromId(washingSieveSizeId, allSieveSizes)?.Caption ?? '63 µm'} by Washing`;
      },
    }),
    convertLabelUomMapToConfig({
      [`${prefix}.WholePortion.Masses.MoistureContent`]: '%',
      [`${prefix}.WholePortion.Masses.AssociatedMoistureContent.ExternalTest.Results.MoistureContentPercent`]: '%',
      [`${prefix}.Results.PercentagePassingWashingSieve`]: '%',
    }),

    getSeparationLabelConfig(`${prefix}.WholePortion.Separation`),
    getSeparationLabelConfig(`${prefix}.WholePortion.Separation.FinePortion.Subspecimen.Separation`)
  );

/**
 * @param prefix prefix for the Separation (e.g. ...WholePortion.Separation)
 */
const getSeparationLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.SeparatingSieveSizeId`]: 'Separating Sieve Size',
    }),
    getPortionLabelConfig(`${prefix}.CoarsePortion`, 'Coarse Portion'),
    getPortionLabelConfig(`${prefix}.FinePortion`, 'Fine Portion'),
    getPortionLabelConfig(`${prefix}.FinePortion.Subspecimen`, 'Subspecimen')
  );

/**
 * @param prefix prefix for the Separation (e.g. ...WholePortion.Separation.CoarsePortion)
 * @param captionPrefix prefix for mass captions (e.g. 'Coarse Portion')
 */
const getPortionLabelConfig = (prefix: string, captionPrefix: string) =>
  convertLabelMapToConfig({
    [`${prefix}.Separation.SeparatingSieveSizeId`]: 'Separating Sieve Size',
    [`${prefix}.Masses.MassMeasurement.Equipment.Balance`]: 'Balance',
    [`${prefix}.Masses.MassMeasurement.Equipment.DryMassBalance`]: 'Balance',
    [`${prefix}.Masses.MassMeasurement.Equipment.Oven`]: 'Oven',
    [`${prefix}.Masses.MassMeasurement.TareAndWetMass`]: `Tare and ${captionPrefix} Wet Mass`,
    [`${prefix}.Masses.MassMeasurement.TareAndDryMass`]: `Tare and ${captionPrefix} Dry Mass`,
    [`${prefix}.Masses.MassMeasurement.TareMass`]: `Tare Mass`,
    [`${prefix}.Masses.WetMass`]: `${captionPrefix} Wet Mass`,
    [`${prefix}.Masses.DryMass`]: `${captionPrefix} Dry Mass`,
    [`${prefix}.Masses.MassUpscaleFactor`]: 'Mass Upscale Factor',
    [`${prefix}.Sieving.Equipment.Balance`]: 'Balance',
    [`${prefix}.Sieving.Equipment.SieveSet`]: 'Sieve Set',
    [`${prefix}.Sieving.ExpectedIndividualPanMassRetained`]: 'Expected Mass Passing',
    [`${prefix}.Washing.WashedDryMass`]: `${captionPrefix} Washed Material Dry Mass`,
  });

export default getLabelConfig;
